import type { IObservableArray } from 'mobx';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FacebookCampaign, FacebookDisplayCreative, IFacebookPage } from '@feathr/blackbox';
import { CardV2 as Card, CountrySelect, EmptyState, Form, Input } from '@feathr/components';

import AdCategorySelect from './FacebookAdCategorySelect';
import FacebookAddCreativeButton from './FacebookAddCreativeButton';
import FacebookAgeSelect from './FacebookAgeSelect';
import FacebookCreativesList from './FacebookCreativesList';
import FacebookCreativeSpecifications from './FacebookCreativeSpecifications';
import FacebookPageSelect from './FacebookPageSelect';

import * as styles from '../../AdWizard/AdWizard.css';

interface IProps {
  campaign: FacebookCampaign;
  creatives: IObservableArray<FacebookDisplayCreative>;
}

function FacebookCreativesStep({ campaign, creatives }: IProps): JSX.Element {
  const { t } = useTranslation();
  const [page, setPage] = useState<IFacebookPage>();

  return (
    <Form className={styles.formRoot} label={'Edit Campaign: Creative'}>
      <Card>
        <Card.Header
          description={t('Select Meta-specific settings and upload your creatives below.')}
          title={t('Creatives')}
        />
        <FacebookCreativeSpecifications />
        <Card.Content addVerticalGap={true}>
          <Input
            attribute={'destination_url'}
            label={t('Destination URL')}
            model={campaign}
            name={'destination_url'}
            placeholder={'https://example.com/landingpage'}
            required={true}
            type={'url'}
          />
          <FacebookPageSelect campaign={campaign} setPage={setPage} />
          <AdCategorySelect campaign={campaign} />
          <CountrySelect
            attribute={'targeted_countries'}
            helpText={t('Select where you want to run this campaign.')}
            isMulti={true}
            label={t('Targeted countries')}
            model={campaign}
            required={true}
          />
          {/* You must have a page selected to view the creatives */}
          <FacebookAgeSelect
            campaign={campaign}
            helpText={t('Select the age range you want to target.')}
            label={t('Age range')}
          />
        </Card.Content>
        <Card.Content addVerticalGap={true}>
          {creatives.length === 0 ? (
            <EmptyState
              description={t('Add a creative to get started')}
              label={t('No creatives added')}
              theme={'slate'}
            >
              <FacebookAddCreativeButton campaign={campaign} creatives={creatives} />
            </EmptyState>
          ) : (
            <>
              <FacebookCreativesList campaign={campaign} creatives={creatives} page={page} />
              <FacebookAddCreativeButton campaign={campaign} creatives={creatives} />
            </>
          )}
        </Card.Content>
      </Card>
    </Form>
  );
}

export default FacebookCreativesStep;
